import { useState, useEffect, useRef, useCallback } from 'react';
import { Send, Search, Plus, User, Loader2, ArrowLeft } from 'lucide-react';
import { messageApi } from '../services/api';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';

const POLL_INTERVAL = 3000; // 3 seconds

function Messages() {
  const { user } = useAuthStore();
  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [showContacts, setShowContacts] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [lastPollTime, setLastPollTime] = useState(null);
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);

  useEffect(() => {
    loadThreads();
    loadContacts();

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Start polling when thread is active
  useEffect(() => {
    if (activeThread) {
      startPolling();
    } else {
      stopPolling();
    }

    return () => stopPolling();
  }, [activeThread]);

  const startPolling = useCallback(() => {
    if (pollIntervalRef.current) return;

    setLastPollTime(new Date().toISOString());

    pollIntervalRef.current = setInterval(async () => {
      if (!lastPollTime) return;

      try {
        const response = await messageApi.poll(lastPollTime);
        const { messages: newMessages, timestamp } = response.data;

        if (newMessages.length > 0) {
          // Add new messages to the current thread
          setMessages(prev => {
            const existingIds = new Set(prev.map(m => m.id));
            const filtered = newMessages.filter(m =>
              m.threadId === activeThread?.id && !existingIds.has(m.id)
            );
            return [...prev, ...filtered.map(m => ({ ...m, isOwn: false }))];
          });

          // Update unread counts in threads list
          loadThreads();
        }

        setLastPollTime(timestamp);
      } catch (error) {
        console.error('Poll error:', error);
      }
    }, POLL_INTERVAL);
  }, [lastPollTime, activeThread]);

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  const loadThreads = async () => {
    try {
      const response = await messageApi.getThreads();
      setThreads(response.data);
    } catch (error) {
      toast.error('Failed to load conversations');
    } finally {
      setIsLoading(false);
    }
  };

  const loadContacts = async () => {
    try {
      const response = await messageApi.getContacts();
      setContacts(response.data);
    } catch (error) {
      console.error('Failed to load contacts:', error);
    }
  };

  const loadThread = async (thread) => {
    try {
      setActiveThread(thread);
      const response = await messageApi.getThread(thread.id);
      setMessages(response.data.messages);
      setLastPollTime(new Date().toISOString());

      // Mark as read
      await messageApi.markAsRead(thread.id);
      loadThreads(); // Refresh to update unread count
    } catch (error) {
      toast.error('Failed to load messages');
    }
  };

  const startNewThread = async (contact) => {
    try {
      const response = await messageApi.createThread(contact.id);
      const thread = {
        id: response.data.id,
        otherParticipant: response.data.otherParticipant,
        lastMessage: null,
        lastMessageAt: null,
        unreadCount: 0
      };

      if (!response.data.isNew) {
        // Thread already exists, just load it
        loadThread(thread);
      } else {
        setThreads(prev => [thread, ...prev]);
        loadThread(thread);
      }

      setShowContacts(false);
    } catch (error) {
      toast.error('Failed to start conversation');
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || !activeThread || isSending) return;

    const content = input.trim();
    setInput('');
    setIsSending(true);

    // Optimistically add message
    const tempMessage = {
      id: Date.now(),
      content,
      isOwn: true,
      createdAt: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempMessage]);

    try {
      const response = await messageApi.sendMessage(activeThread.id, content);
      // Replace temp message with real one
      setMessages(prev =>
        prev.map(m => m.id === tempMessage.id ? response.data : m)
      );
      loadThreads(); // Update thread list
    } catch (error) {
      // Remove temp message on error
      setMessages(prev => prev.filter(m => m.id !== tempMessage.id));
      toast.error('Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const filteredContacts = contacts.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex">
      {/* Thread List */}
      <div className={`w-full md:w-80 border-r bg-white flex flex-col ${activeThread ? 'hidden md:flex' : 'flex'}`}>
        <div className="p-4 border-b">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-semibold">Messages</h1>
            <button
              onClick={() => setShowContacts(true)}
              className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
              title="New Message"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {threads.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              <User className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No conversations yet</p>
              <button
                onClick={() => setShowContacts(true)}
                className="mt-3 text-primary-600 hover:underline text-sm"
              >
                Start a conversation
              </button>
            </div>
          ) : (
            <div className="divide-y">
              {threads.map(thread => (
                <button
                  key={thread.id}
                  onClick={() => loadThread(thread)}
                  className={`w-full p-4 text-left hover:bg-gray-50 transition-colors ${
                    activeThread?.id === thread.id ? 'bg-primary-50' : ''
                  }`}
                >
                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-primary-700 font-medium">
                        {thread.otherParticipant.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="ml-3 flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-gray-900 truncate">
                          {thread.otherParticipant.name}
                        </p>
                        {thread.lastMessageAt && (
                          <span className="text-xs text-gray-400 ml-2">
                            {formatTime(thread.lastMessageAt)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-sm text-gray-500 truncate">
                          {thread.lastMessage || 'No messages yet'}
                        </p>
                        {thread.unreadCount > 0 && (
                          <span className="ml-2 bg-primary-600 text-white text-xs rounded-full px-2 py-0.5">
                            {thread.unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className={`flex-1 flex flex-col bg-gray-50 ${!activeThread ? 'hidden md:flex' : 'flex'}`}>
        {activeThread ? (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b bg-white flex items-center">
              <button
                onClick={() => setActiveThread(null)}
                className="md:hidden p-2 mr-2 text-gray-500 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                <span className="text-primary-700 font-medium">
                  {activeThread.otherParticipant.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="ml-3">
                <p className="font-medium text-gray-900">
                  {activeThread.otherParticipant.name}
                </p>
                <p className="text-sm text-gray-500 capitalize">
                  {activeThread.otherParticipant.role}
                </p>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.length === 0 ? (
                <div className="text-center text-gray-500 py-12">
                  <p>No messages yet. Start the conversation!</p>
                </div>
              ) : (
                messages.map((msg, idx) => (
                  <div
                    key={msg.id || idx}
                    className={`flex ${msg.isOwn ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[75%] rounded-2xl px-4 py-2 ${
                        msg.isOwn
                          ? 'bg-primary-600 text-white'
                          : 'bg-white border border-gray-200'
                      }`}
                    >
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                      <p className={`text-xs mt-1 ${msg.isOwn ? 'text-primary-200' : 'text-gray-400'}`}>
                        {formatTime(msg.createdAt)}
                      </p>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <form onSubmit={sendMessage} className="p-4 border-t bg-white">
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  disabled={isSending}
                />
                <button
                  type="submit"
                  disabled={isSending || !input.trim()}
                  className="p-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSending ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </button>
              </div>
            </form>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            <div className="text-center">
              <User className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg">Select a conversation</p>
              <p className="text-sm mt-1">or start a new one</p>
            </div>
          </div>
        )}
      </div>

      {/* New Message Modal */}
      {showContacts && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[80vh] flex flex-col">
            <div className="p-4 border-b">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold">New Message</h2>
                <button
                  onClick={() => setShowContacts(false)}
                  className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                >
                  &times;
                </button>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search contacts..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {filteredContacts.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  {searchQuery ? 'No contacts found' : 'No contacts available'}
                </div>
              ) : (
                <div className="divide-y">
                  {filteredContacts.map(contact => (
                    <button
                      key={contact.id}
                      onClick={() => startNewThread(contact)}
                      className="w-full p-4 text-left hover:bg-gray-50 transition-colors flex items-center"
                    >
                      <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                        <span className="text-primary-700 font-medium">
                          {contact.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="ml-3">
                        <p className="font-medium text-gray-900">{contact.name}</p>
                        <p className="text-sm text-gray-500 capitalize">{contact.role}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Messages;
